import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Button,
  alpha,
  Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CodeIcon from '@mui/icons-material/Code';
import TerminalIcon from '@mui/icons-material/Terminal';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import TimelineIcon from '@mui/icons-material/Timeline';
import { useNavigate } from 'react-router-dom';

const ScriptsPage = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      maxWidth: '1400px',
      margin: '0 auto',
      p: { xs: 2, sm: 3 },
    }}>
      {/* Header */}
      <Box sx={{ 
        mb: 3,
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', md: 'center' },
        gap: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton 
            onClick={() => navigate('/app')} 
            sx={{
              color: 'text.secondary',
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
              '&:hover': {
                color: 'primary.main',
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08)
              },
              borderRadius: 2
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h4" sx={{ 
                fontFamily: 'StrawberrySansDisplay',
                fontWeight: 500,
                color: 'text.primary',
                fontSize: { xs: '1.5rem', sm: '2rem' }
              }}>
                Scripts
              </Typography>
              <Chip 
                label="Coming Soon" 
                color="secondary"
                size="small"
                sx={{ 
                  ml: 1,
                  fontWeight: 'medium', 
                  fontSize: '0.7rem',
                  height: 24,
                  bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.15),
                  color: 'secondary.main',
                  border: '1px solid',
                  borderColor: (theme) => alpha(theme.palette.secondary.main, 0.3),
                }}
              />
            </Box>
            <Typography variant="body1" color="text.secondary">
              Administrator tools
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
        p: { xs: 2, sm: 4, md: 6 },
        mb: 2,
        borderRadius: 4,
        background: (theme) => `
          radial-gradient(circle at 90% 90%, ${alpha(theme.palette.primary.main, 0.06)}, transparent 50%),
          radial-gradient(circle at 10% 10%, ${alpha(theme.palette.secondary.main, 0.06)}, transparent 50%),
          ${theme.palette.background.paper}
        `,
        border: '1px solid',
        borderColor: 'divider',
      }}>
        <Box
          sx={{
            position: 'relative',
            mb: 4,
            '&::before': {
              content: '""',
              position: 'absolute',
              width: '200%',
              height: '200%',
              top: '-50%',
              left: '-50%',
              background: (theme) => `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 70%)`,
              borderRadius: '50%',
              zIndex: 0
            }
          }}
        >
          <TerminalIcon 
            sx={{ 
              fontSize: 100,
              color: 'primary.main',
              opacity: 0.9,
              position: 'relative',
              zIndex: 1
            }} 
          />
        </Box>

        <Typography variant="h3" sx={{ 
          mb: 2,
          fontWeight: 'bold',
          fontFamily: 'StrawberrySansDisplay',
          color: 'text.primary',
          position: 'relative',
          display: 'inline-block'
        }}>
          Scripts
        </Typography>
        
        <Typography variant="h6" sx={{ 
          mb: 4,
          maxWidth: 600,
          color: 'text.secondary',
          lineHeight: 1.6,
          fontWeight: 'normal'
        }}>
          Exciting new capabilities coming to streamline your workflow and enhance productivity. 
          Our team is hard at work building the future of hotel management automation.
        </Typography>

        {/* Feature Highlights */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
          gap: 3,
          width: '100%',
          maxWidth: 900,
          mb: 5
        }}>
          <FeatureCard 
            icon={<CodeIcon fontSize="large" />}
            title="Run Scripts"
            description="Execute predefined operations"
          />
          <FeatureCard 
            icon={<AutoFixHighIcon fontSize="large" />}
            title="Automate Tasks"
            description="Simplify repetitive processes"
          />
          <FeatureCard 
            icon={<TimelineIcon fontSize="large" />}
            title="Monitor Results"
            description="View operation outcomes"
          />
        </Box>
        
        <Button 
          variant="contained" 
          color="primary"
          size="large"
          disabled
          sx={{ 
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '1rem',
            borderRadius: 2,
            px: 4,
            py: 1.5
          }}
        >
          Coming Soon
        </Button>
      </Box>
    </Box>
  );
};

// Feature card component
const FeatureCard = ({ icon, title, description }) => (
  <Paper 
    elevation={0}
    sx={{
      p: 3,
      borderRadius: 3,
      bgcolor: 'background.default',
      border: '1px solid',
      borderColor: 'divider',
      transition: 'all 0.3s ease',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      '&:hover': {
        transform: 'translateY(-5px)',
        boxShadow: (theme) => `0 8px 16px ${alpha(theme.palette.primary.main, 0.1)}`,
        borderColor: (theme) => alpha(theme.palette.primary.main, 0.3)
      }
    }}
  >
    <Box 
      sx={{ 
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
        color: 'primary.main', 
        p: 1.5,
        borderRadius: 2,
        mb: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      {icon}
    </Box>
    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
      {title}
    </Typography>
    <Typography variant="body2" color="text.secondary" align="center">
      {description}
    </Typography>
  </Paper>
);

export default ScriptsPage; 